import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Grid, P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import store from 'state/create-store'
import { showModal } from 'state/actions'

import useItemState from 'lib/effects/item-state'
import useProbateEstateItemHelpers from 'lib/effects/probate-estate-item-helpers'

import EstateCard from 'components/estate-card'
import InputFloating from 'components/form/input-floating'
import CircularRadioGroup from 'components/form/circular-radio-group'
import GridWithToggle from 'components/grid-with-toggle'

import DeleteModal from '../components/delete-modal'
import TextSpacer from '../components/text-spacer'
import withForm from '../components/with-form'

import { NATIONAL_SAVINGS_AND_INVESTMENTS_SECTION_ACTIVE } from './field-names'

import { useFormikContext } from 'formik'
import namespace from 'lib/formik/namespace'
import { getItemsTotals } from '../get-totals'
import get from 'lodash/get'

import {
  DECEASED_SHARE_OF_JOINT_OWNERSHIP,
  DESCRIPTION,
  IS_ESTIMATED_VALUE,
  IS_ESTIMATED_VALUE_OPTIONS,
  JOINT_OWNER_NAME,
  OWNERSHIP,
  VALUE,
} from '../item-field-constants'
import NotesInput from 'components/notes-input'
import { NOTES } from './field-names'

const Item = ({
  item,
  probateEstateId,
  index,
  listLength,
  deleteItem,
  updateItem,
}) => {
  const { withNamespace, withoutNamespace } = namespace(
    `nationalSavingsAndInvestments.${item.id}.`
  )
  const { values } = useFormikContext()

  const handleSave = ({ name, value }) => {
    updateItem({
      id: item.id,
      attributes: { [withoutNamespace(name)]: value || null },
    })
  }

  const onTrashClick = () => {
    store.dispatch(
      showModal({
        component: DeleteModal,
        headingText: 'Delete NS&I account',
        config: {
          deleteCallback: () => {
            deleteItem({
              probateEstateId,
              id: item.id,
            })
          },
          type: 'NS&I account',
          resourceId: item.id,
        },
      })
    )
  }

  return (
    <EstateCard
      onTrashClick={onTrashClick}
      listLength={listLength}
      listPosition={index + 1}
      key={item.id}
      type="NS&amp;I account"
    >
      <Grid style={{ gridAutoFlow: 'row' }}>
        <Grid.Item span={6}>
          <InputFloating
            name={withNamespace(DESCRIPTION)}
            label="Description"
            handleSave={handleSave}
            hint="If IHT400, we’ll need sort code and account number"
          />
        </Grid.Item>
        <Grid.Item span={3} startColumn={1}>
          <InputFloating
            name={withNamespace(VALUE)}
            label="Total value (£)"
            handleSave={handleSave}
            isCurrency
          />
        </Grid.Item>
        <Grid.Item
          span={9}
          style={{ alignSelf: 'center', justifySelf: 'center' }}
        >
          <CircularRadioGroup
            name={withNamespace(IS_ESTIMATED_VALUE)}
            options={IS_ESTIMATED_VALUE_OPTIONS}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        <Grid.Item>
          <CircularRadioGroup
            name={withNamespace(OWNERSHIP)}
            label="Ownership"
            options={[
              { label: 'Not asked', value: '' },
              { label: 'Sole', value: 'sole' },
              { label: 'Joint', value: 'joint' },
            ]}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        {get(values, withNamespace(OWNERSHIP)) === 'joint' && (
          <>
            <Grid.Item span={6}>
              <InputFloating
                name={withNamespace(JOINT_OWNER_NAME)}
                label="Name of joint owner"
                handleSave={handleSave}
                hint="If more than one, leave a note below"
              />
            </Grid.Item>
            <Grid.Item span={6}>
              <InputFloating
                name={withNamespace(DECEASED_SHARE_OF_JOINT_OWNERSHIP)}
                label="Share owned by person who's died (%)"
                handleSave={handleSave}
              />
            </Grid.Item>
          </>
        )}
        <Grid.Item>
          <NotesInput name={withNamespace(NOTES)} handleSave={handleSave} />
        </Grid.Item>
      </Grid>
    </EstateCard>
  )
}

const ItemWithForm = withForm(Item)

const NationalSavingsAndInvestments = ({
  initialItems,
  probateEstateId,
  saveProbateEstateField,
}) => {
  const { items, addItem, removeItem, replaceItem } = useItemState(initialItems)

  const {
    createItem,
    createdItem,
    isCreating,
    deleteItem,
    deletedItem,
    updateItem,
    updatedItem,
  } = useProbateEstateItemHelpers({ probateEstateId })

  useEffect(() => {
    if (createdItem) addItem(createdItem)
  }, [addItem, createdItem])

  useEffect(() => {
    if (deletedItem) removeItem(deletedItem)
  }, [removeItem, deletedItem])

  useEffect(() => {
    if (updatedItem) replaceItem(updatedItem)
  }, [replaceItem, updatedItem])

  const { total, sole, joint, unspecified } = getItemsTotals(items)
  return (
    <GridWithToggle
      disabled={items.length > 0}
      name={NATIONAL_SAVINGS_AND_INVESTMENTS_SECTION_ACTIVE}
      onToggle={saveProbateEstateField}
      scrollId="4.4"
      title="NS&amp;I"
    >
      <Grid.Item>
        <P size="S">
          Total: {total} <TextSpacer /> Sole: {sole} <TextSpacer /> Joint share:{' '}
          {joint} <TextSpacer /> Unspecified: {unspecified}
        </P>

        {items.map((item, index) => (
          <ItemWithForm
            deleteItem={deleteItem}
            enableReinitialize={false}
            index={index}
            initialValues={{
              nationalSavingsAndInvestments: { [item.id]: item.attributes },
            }}
            item={item}
            key={item.id}
            listLength={items.length}
            probateEstateId={probateEstateId}
            updateItem={updateItem}
          />
        ))}
      </Grid.Item>
      <Grid.Item>
        <Button.Secondary
          style={{ paddingTop: GTR.XXS, paddingBottom: GTR.XXS }}
          loading={isCreating}
          type="button"
          onClick={() => {
            createItem({ itemType: 'national_savings_and_investments' })
          }}
        >
          Add NS&amp;I account
        </Button.Secondary>
      </Grid.Item>
    </GridWithToggle>
  )
}

NationalSavingsAndInvestments.propTypes = {
  initialItems: PropTypes.array,
  probateEstateId: PropTypes.string.isRequired,
  saveProbateEstateField: PropTypes.func.isRequired,
}

NationalSavingsAndInvestments.defaultProps = {
  initialItems: [],
}

export default NationalSavingsAndInvestments
